<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import apiClient from "@/apiClient.js";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import ReferenceTabNavigation from "@/components/reference/ReferenceTabNavigation.vue";
import EditButton from "@/components/EditButton.vue";
import { usePagination } from "@/composables/usePagination";
import { usePresentationMode } from "@/composables/usePresentationMode";
import dayjs from "dayjs";
import { useRoute, useRouter } from "vue-router";
import { MagnifyingGlassIcon } from "@heroicons/vue/24/solid";

const list = useListStoreNew();
const { maskPoundAmount } = usePresentationMode();
const route = useRoute();
const router = useRouter();
const tabs = [
  { id: "browse", label: "Browse / Search Transactions" },
  { id: "import", label: "Import Transactions" },
];
const activeTab = computed(() => route.query.tab === "import" ? "import" : "browse");
const activeTabLabel = computed(() => tabs.find((tab) => tab.id === activeTab.value)?.label);
const selectTab = (tab) => router.push({ query: { ...route.query, tab } });
let syncingRoute = false;
const filter = ref(route.query?.af ?? "");
const searchTerm = ref(filter.value);
const flowFilter = ref(route.query?.flow ?? "");
const startDate = ref(route.query?.start_date ?? "");
const endDate = ref(route.query?.end_date ?? "");
const uploadError = ref("");
const isUploading = ref(false);
const importSummary = ref(null);
const previewRows = ref([]);
const importConflicts = ref([]);
const fileInput = ref(null);
const perPageOptions = [25, 50, 100];
const initialPerPage = Number(route.query?.per_page);
const perPage = ref(perPageOptions.includes(initialPerPage) ? initialPerPage : 25);
const modalOpen = ref(false);
const modalSaving = ref(false);
const activeTransaction = ref(null);
const modalEditing = ref(false);
const modalForm = reactive({
  supplier: null,
  requisition: null,
  notes: "",
  type: "",
  flow: "out",
  amount: 0,
});
const supplierInputKey = ref(0);
const requisitionInputKey = ref(0);
const summaryLoading = ref(false);
const summaryError = ref("");
const summary = ref({
  monthly_in: [],
  monthly_out: [],
  year_over_year_in: [],
  year_over_year_out: [],
});
const searchDebounce = ref(null);
const flowOptions = [
  { value: "", label: "All flows" },
  { value: "in", label: "In" },
  { value: "out", label: "Out" },
];

const { setUrl, changePage } = usePagination(list, filter, () => ({
  per_page: perPage.value,
  flow: flowFilter.value || undefined,
  start_date: startDate.value || undefined,
  end_date: endDate.value || undefined,
}));

const formatCurrency = (value) => {
  if (value === null || value === undefined) {
    return maskPoundAmount("£0.00");
  }

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(value / 100);
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  return dayjs(value).format("DD MMM YYYY");
};

const monthlyInBars = computed(() => summary.value.monthly_in || []);
const totalBankIn = computed(() =>
  monthlyInBars.value.reduce((sum, entry) => sum + (entry?.total ?? 0), 0),
);
const totalBankOut = computed(() =>
  (summary.value.monthly_out || []).reduce((sum, entry) => sum + (entry?.total ?? 0), 0),
);
const netBankFlow = computed(() => totalBankIn.value - totalBankOut.value);
const tableTotals = computed(() => {
  const rows = list.getIndex || [];
  return rows.reduce(
    (acc, row) => {
      acc.paidOut += row?.paid_out ?? 0;
      acc.paidIn += row?.paid_in ?? 0;
      return acc;
    },
    { paidOut: 0, paidIn: 0 },
  );
});
const hasFilters = computed(() =>
  Boolean(filter.value || flowFilter.value || startDate.value || endDate.value),
);

const applyFilters = () => {
  list.clearFilters();
  if (filter.value?.trim()) {
    list.addFilter("search", encodeURIComponent(filter.value.trim()));
  }
  if (flowFilter.value) {
    list.addFilter("flow", flowFilter.value);
  }
  if (startDate.value) {
    list.addFilter("start_date", startDate.value);
  }
  if (endDate.value) {
    list.addFilter("end_date", endDate.value);
  }
  list.addFilter("per_page", perPage.value);
};

const runSearch = () => {
  applyFilters();
  list.resetPage();
  setUrl();
};

const onSearch = () => {
  clearTimeout(searchDebounce.value);
  filter.value = searchTerm.value?.trim() || "";
  runSearch();
};

const resetSearch = () => {
  searchTerm.value = "";
  filter.value = "";
  flowFilter.value = "";
  startDate.value = "";
  endDate.value = "";
  runSearch();
};

const handleFileChange = async (event) => {
  const files = Array.from(event.target.files || []).filter(Boolean);
  if (!files.length) {
    return;
  }

  await importFiles(files);
  event.target.value = "";
};

const importFiles = async (files) => {
  uploadError.value = "";
  importSummary.value = null;
  previewRows.value = [];
  importConflicts.value = [];
  isUploading.value = true;

  const combinedSummary = {
    processed: 0,
    created: 0,
    duplicates: 0,
    skipped: 0,
    conflicts: 0,
  };
  let combinedPreview = [];
  let combinedConflicts = [];

  try {
    for (const file of files) {
      const result = await uploadFile(file);
      const summary = result?.summary ?? {};
      combinedSummary.processed += summary.processed ?? 0;
      combinedSummary.created += summary.created ?? 0;
      combinedSummary.duplicates += summary.duplicates ?? 0;
      combinedSummary.skipped += summary.skipped ?? 0;
      combinedSummary.conflicts += summary.conflicts ?? 0;
      combinedPreview = combinedPreview.concat(result?.preview ?? []);
      combinedConflicts = combinedConflicts.concat(result?.conflicts ?? []);
    }

    importSummary.value = combinedSummary;
    previewRows.value = combinedPreview.slice(0, 10);
    importConflicts.value = combinedConflicts.slice(0, 25);
    list.resetPage();
    setUrl();
  } catch (error) {
    uploadError.value = error?.response?.data?.errors?.file?.[0]
      || error?.response?.data?.message
      || error?.message
      || "Unable to import the selected file.";
  } finally {
    isUploading.value = false;
  }
}; 

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post("/api/bank-transactions/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  if (!response?.data) {
    throw new Error("The server response was empty.");
  }

  return response.data;
};

const clearSummary = () => {
  importSummary.value = null;
  previewRows.value = [];
  importConflicts.value = [];
};

const openTransactionModal = (transaction) => {
  activeTransaction.value = transaction;
  modalForm.notes = transaction.notes ?? "";
  modalForm.supplier = transaction.supplier ? { ...transaction.supplier } : null;
  modalForm.requisition = transaction.requisition ? { ...transaction.requisition } : null;
  modalForm.type = transaction.type ?? "";
  modalForm.amount = transaction.paid_in ?? transaction.paid_out ?? 0;
  modalForm.flow = (transaction.paid_in ?? 0) > 0 ? "in" : "out";
  supplierInputKey.value++;
  requisitionInputKey.value++;
  modalEditing.value = false;
  modalOpen.value = true;
};

const closeTransactionModal = () => {
  modalOpen.value = false;
  activeTransaction.value = null;
  modalEditing.value = false;
};
const enableEditing = () => {
  modalEditing.value = true;
};

const supplierOutput = (supplier) => supplier?.company ?? "";
const requisitionOutput = (req) => {
  if (!req) {
    return "";
  }
  return `#${req.id} · ${req.description}`;
};

const selectSupplier = (supplier) => {
  modalForm.supplier = supplier ?? null;
};

const clearSupplier = () => {
  modalForm.supplier = null;
  supplierInputKey.value++;
};

const selectRequisition = (req) => {
  modalForm.requisition = req ?? null;
};

const clearRequisition = () => {
  modalForm.requisition = null;
  requisitionInputKey.value++;
};

const replaceTransactionRow = (updated) => {
  const payload = list.index.value;
  const rows = payload?.data;
  if (!rows) {
    return;
  }
  const index = rows.findIndex((row) => row.id === updated.id);
  if (index === -1) {
    return;
  }
  rows[index] = updated;
  list.index.value = {
    ...payload,
    data: [...rows],
  };
};

const saveTransactionDetails = async () => {
  if (!activeTransaction.value) {
    return;
  }

  const normalizedAmount = Math.max(0, Math.round(Number(modalForm.amount) || 0));
  const paidIn = modalForm.flow === "in" ? normalizedAmount : null;
  const paidOut = modalForm.flow === "out" ? normalizedAmount : null;

  modalSaving.value = true;
  try {
    const { data } = await apiClient.post(`/api/bank-transactions/${activeTransaction.value.id}`, {
      _method: "PUT",
      supplier_id: modalForm.supplier?.id,
      requisition_id: modalForm.requisition?.id,
      notes: modalForm.notes,
      type: modalForm.type || null,
      paid_in: paidIn,
      paid_out: paidOut,
    });
    replaceTransactionRow(data);
    closeTransactionModal();
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to update transaction.");
  } finally {
    modalSaving.value = false;
  }
};

onMounted(() => {
  list.configure({ remote: "/api/bank-transactions", local: "/bank-transactions" });
  applyFilters();
  list.listService.setPage(Number(route.query.page) || 1);
  list.fetchIndex();
  loadSummary();
});

watch(perPage, (newValue, oldValue) => {
  if (syncingRoute || newValue === oldValue) {
    return;
  }
  applyFilters();
  list.resetPage();
  setUrl();
}, { flush: "sync" });

watch(searchTerm, (newValue, oldValue) => {
  if (syncingRoute || newValue === oldValue) {
    return;
  }
  if (searchDebounce.value) {
    clearTimeout(searchDebounce.value);
  }
  searchDebounce.value = setTimeout(() => {
    filter.value = newValue?.trim() || "";
    runSearch();
  }, 400);
}, { flush: "sync" });

// Query changes no longer remount this page, so restore filters on Back/Forward.
watch(() => route.query, (query, previousQuery) => {
  const filterKeys = ["af", "flow", "start_date", "end_date", "per_page", "page"];
  if (filterKeys.every((key) => query[key] === previousQuery[key])) return;
  const nextPerPage = perPageOptions.includes(Number(query.per_page)) ? Number(query.per_page) : 25;
  const nextPage = Number(query.page) || 1;
  if ((query.af ?? "") === filter.value && (query.flow ?? "") === flowFilter.value
    && (query.start_date ?? "") === startDate.value && (query.end_date ?? "") === endDate.value
    && nextPerPage === perPage.value && nextPage === Number(list.getPage)) return;

  clearTimeout(searchDebounce.value);
  syncingRoute = true;
  filter.value = query.af ?? "";
  searchTerm.value = filter.value;
  flowFilter.value = query.flow ?? "";
  startDate.value = query.start_date ?? "";
  endDate.value = query.end_date ?? "";
  perPage.value = nextPerPage;
  syncingRoute = false;
  applyFilters();
  list.listService.setPage(nextPage);
  list.fetchIndex();
});

onBeforeUnmount(() => clearTimeout(searchDebounce.value));

const loadSummary = async () => {
  summaryLoading.value = true;
  summaryError.value = "";
  try {
    const { data } = await apiClient.get("/api/bank-transactions/summary");
    summary.value = {
      monthly_in: data?.monthly_in ?? [],
      monthly_out: data?.monthly_out ?? [],
      year_over_year_in: data?.year_over_year_in ?? [],
      year_over_year_out: data?.year_over_year_out ?? [],
    };
  } catch (error) {
    summaryError.value = "Unable to load bank transaction analytics.";
  } finally {
    summaryLoading.value = false;
  }
};
</script>

<template>
  <div class="bank-transactions-workspace space-y-6">
    <ReferenceTabNavigation :tabs="tabs" :active-tab="activeTab"
      id-prefix="bank-transactions" label="Bank transaction actions" @select="selectTab" />
    <div id="bank-transactions-tab-panel" role="tabpanel" :aria-label="activeTabLabel" tabindex="0" class="space-y-6">
    <section v-show="activeTab === 'browse'" class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex flex-col gap-2">
        <p class="text-xs uppercase tracking-[0.25em] text-slate-500">Bank Analytics</p>
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Rolling 12-month totals
        </h2>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          Snapshot of cash in, cash out, and net flow over the last year.
        </p>
      </div>

      <div v-if="summaryError" class="mt-4 text-sm text-rose-600">{{ summaryError }}</div>

      <div class="mt-6 grid gap-4 md:grid-cols-3">
        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
          <p class="text-xs uppercase tracking-wide text-slate-500">Cash in</p>
          <p class="mt-2 text-2xl font-semibold text-emerald-600">
            {{ formatCurrency(totalBankIn) }}
          </p>
          <p class="mt-1 text-xs text-slate-400">Last 12 months</p>
        </div>
        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
          <p class="text-xs uppercase tracking-wide text-slate-500">Cash out</p>
          <p class="mt-2 text-2xl font-semibold text-rose-500">
            {{ formatCurrency(totalBankOut) }}
          </p>
          <p class="mt-1 text-xs text-slate-400">Last 12 months</p>
        </div>
        <div class="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
          <p class="text-xs uppercase tracking-wide text-slate-500">Net flow</p>
          <p class="mt-2 text-2xl font-semibold" :class="netBankFlow >= 0 ? 'text-emerald-600' : 'text-rose-500'">
            {{ formatCurrency(netBankFlow) }}
          </p>
          <p class="mt-1 text-xs text-slate-400">In minus out</p>
        </div>
      </div>
    </section>

    <div v-show="activeTab === 'import'" class="space-y-6">
    <form-box header="Import Bank Transactions">
      <div class="space-y-4">
        <p class="text-sm text-slate-600 dark:text-slate-300">
          Upload a CSV exported from your bank. The importer expects columns for
          <strong>Date</strong>, <strong>Description</strong>, <strong>Paid Out</strong>, and
          <strong>Paid In</strong>. <strong>Type</strong> is optional; credit card statements omit it
          and are tagged as <strong>Credit Card</strong>. Duplicate rows are ignored using Date,
          Description, Paid Out, and Paid In.
        </p>
        <div class="flex flex-wrap items-center gap-3">
          <label for="bank-transaction-upload"
            class="btn accent cursor-pointer inline-flex items-center gap-2 disabled:opacity-60"
            :class="{ 'opacity-60 cursor-not-allowed': isUploading }">
            <span v-if="!isUploading">Select CSV</span>
            <span v-else>Uploading…</span>
          </label>
          <input id="bank-transaction-upload" ref="fileInput" accept=".csv,text/csv"
            class="hidden" type="file" multiple @change="handleFileChange">
          <button v-if="importSummary || previewRows.length" class="btn" @click.prevent="clearSummary">
            Clear Results
          </button>
        </div>
        <p v-if="uploadError" class="text-sm text-rose-600">
          {{ uploadError }}
        </p>
        <div v-if="importSummary" class="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div class="p-4 bg-slate-100 dark:bg-slate-800 rounded">
            <div class="text-xs uppercase text-slate-500">Processed</div>
            <div class="text-2xl font-semibold">{{ importSummary.processed }}</div>
          </div>
          <div class="p-4 bg-emerald-100 dark:bg-emerald-900 rounded">
            <div class="text-xs uppercase text-slate-500">Created</div>
            <div class="text-2xl font-semibold text-emerald-700 dark:text-emerald-200">
              {{ importSummary.created }}
            </div>
          </div>
          <div class="p-4 bg-amber-100 dark:bg-amber-900 rounded">
            <div class="text-xs uppercase text-slate-500">Duplicates</div>
            <div class="text-2xl font-semibold text-amber-700 dark:text-amber-200">
              {{ importSummary.duplicates }}
            </div>
          </div>
          <div class="p-4 bg-slate-200 dark:bg-slate-900 rounded">
            <div class="text-xs uppercase text-slate-500">Skipped</div>
            <div class="text-2xl font-semibold">{{ importSummary.skipped }}</div>
          </div>
          <div class="p-4 bg-rose-100 dark:bg-rose-900 rounded">
            <div class="text-xs uppercase text-slate-500">Conflicts</div>
            <div class="text-2xl font-semibold text-rose-700 dark:text-rose-200">
              {{ importSummary.conflicts }}
            </div>
          </div>
        </div>
      </div>
    </form-box>

    <form-box v-if="importConflicts.length" header="Import Conflicts" :collapsible="true">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left text-slate-500 uppercase text-xs">
              <th class="px-2 py-1">Row</th>
              <th class="px-2 py-1">Issue</th>
              <th class="px-2 py-1">Date</th>
              <th class="px-2 py-1">Description</th>
              <th class="px-2 py-1 text-right">Paid Out</th>
              <th class="px-2 py-1 text-right">Paid In</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="conflict in importConflicts" :key="`${conflict.row}-${conflict.message}`"
              class="border-t border-slate-200 dark:border-slate-700">
              <td class="px-2 py-2 whitespace-nowrap">{{ conflict.row }}</td>
              <td class="px-2 py-2">{{ conflict.message }}</td>
              <td class="px-2 py-2 whitespace-nowrap">{{ formatDate(conflict.transaction_date) }}</td>
              <td class="px-2 py-2">{{ conflict.description ?? "—" }}</td>
              <td class="px-2 py-2 text-right">{{ formatCurrency(conflict.paid_out) }}</td>
              <td class="px-2 py-2 text-right">{{ formatCurrency(conflict.paid_in) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </form-box>

    <form-box v-if="previewRows.length" header="Preview of Recent Imports" :collapsible="true">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left text-slate-500 uppercase text-xs">
              <th class="px-2 py-1">Date</th>
              <th class="px-2 py-1">Description</th>
              <th class="px-2 py-1">Type</th>
              <th class="px-2 py-1 text-right">Paid Out</th>
              <th class="px-2 py-1 text-right">Paid In</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in previewRows" :key="row.id" class="border-t border-slate-200 dark:border-slate-700">
              <td class="px-2 py-2 whitespace-nowrap">{{ formatDate(row.transaction_date) }}</td>
              <td class="px-2 py-2">{{ row.description }}</td>
              <td class="px-2 py-2">{{ row.type ?? "—" }}</td>
              <td class="px-2 py-2 text-right">{{ formatCurrency(row.paid_out) }}</td>
              <td class="px-2 py-2 text-right">{{ formatCurrency(row.paid_in) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </form-box>

    </div>
    <div v-show="activeTab === 'browse'" class="space-y-6">
    <form-box header="Transactions">
      <div class="flex flex-col gap-3">
        <div class="flex flex-col lg:flex-row gap-3">
          <input v-model="searchTerm" type="text" placeholder="Search by description or type"
            class="input flex-1" @keyup.enter="onSearch">
          <div class="flex gap-2 items-center">
            <select v-model.number="perPage" class="input w-32">
              <option v-for="option in perPageOptions" :key="option" :value="option">
                {{ option }} / page
              </option>
            </select>
            <button class="btn accent" @click.prevent="onSearch">
              <MagnifyingGlassIcon class="icon" />
            </button>
            <button v-if="hasFilters" class="btn" @click.prevent="resetSearch">
              Clear
            </button>
          </div>
        </div>
        <div class="flex flex-col lg:flex-row gap-3 lg:items-center">
          <select v-model="flowFilter" class="input w-40">
            <option v-for="option in flowOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <div class="flex flex-col sm:flex-row gap-2 items-center">
            <input v-model="startDate" type="date" class="input w-44">
            <span class="text-xs uppercase tracking-wide text-slate-400">to</span>
            <input v-model="endDate" type="date" class="input w-44">
            <button class="btn accent px-3" aria-label="Search transactions" @click.prevent="onSearch">
              <MagnifyingGlassIcon class="icon" />
            </button>
          </div>
        </div>
      </div>
    </form-box>

    <table class="box list" v-if="!list.isLoading">
      <thead>
        <tr class="text-left text-xs uppercase text-slate-500">
          <th class="px-3 py-2">Date</th>
          <th class="px-3 py-2">Description</th>
          <th class="px-3 py-2 hidden md:table-cell">Type</th>
          <th class="px-3 py-2 text-right hidden lg:table-cell">Paid Out</th>
          <th class="px-3 py-2 text-right hidden lg:table-cell">Paid In</th>
          <th class="px-3 py-2 hidden xl:table-cell">Supplier</th>
          <th class="px-3 py-2 hidden xl:table-cell">Requisition</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in list.getIndex" :key="row.id"
          class="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
          @click="() => openTransactionModal(row)">
          <td class="px-3 py-3 whitespace-nowrap">{{ formatDate(row.transaction_date) }}</td>
          <td class="px-3 py-3">
            <div class="font-medium text-slate-900 dark:text-slate-100">
              {{ row.description }}
            </div>
            <div class="text-xs text-slate-500 lg:hidden">
              {{ formatCurrency(row.paid_in ?? row.paid_out ?? 0) }}
            </div>
          </td>
          <td class="px-3 py-3 hidden md:table-cell">
            <span class="label-clickable bg-slate-600 w-20 text-center uppercase">
              {{ row.type ?? '—' }}
            </span>
          </td>
          <td class="px-3 py-3 text-right hidden lg:table-cell">{{ formatCurrency(row.paid_out) }}</td>
          <td class="px-3 py-3 text-right hidden lg:table-cell">{{ formatCurrency(row.paid_in) }}</td>
          <td class="px-3 py-3 hidden xl:table-cell">
            {{ row.supplier?.company ?? "Not linked" }}
          </td>
          <td class="px-3 py-3 hidden xl:table-cell">
            {{ row.requisition?.description ?? "Not linked" }}
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr v-if="list.getIndex.length">
          <td class="px-3 py-3 text-xs uppercase text-slate-500">Total</td>
          <td class="px-3 py-3">
            <div class="flex flex-col gap-1 text-sm text-slate-600 lg:hidden">
              <span>Out: {{ formatCurrency(tableTotals.paidOut) }}</span>
              <span>In: {{ formatCurrency(tableTotals.paidIn) }}</span>
            </div>
          </td>
          <td class="px-3 py-3 hidden md:table-cell"></td>
          <td class="px-3 py-3 text-right hidden lg:table-cell font-semibold">
            {{ formatCurrency(tableTotals.paidOut) }}
          </td>
          <td class="px-3 py-3 text-right hidden lg:table-cell font-semibold">
            {{ formatCurrency(tableTotals.paidIn) }}
          </td>
          <td class="px-3 py-3 hidden xl:table-cell"></td>
          <td class="px-3 py-3 hidden xl:table-cell"></td>
        </tr>
        <tr>
          <td colspan="7" class="px-3 py-4" v-if="list.getIndex.length < 1">
            No transactions found.
          </td>
        </tr>
        <tr>
          <th colspan="7" class="px-3 py-4">
            <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)"
              @next="changePage(1)" />
          </th>
        </tr>
      </tfoot>
    </table>

    <table class="box list" v-else>
      <tbody>
        <tr v-for="n in 8" :key="n" class="border-b border-gray-300 animate-pulse">
          <td class="p-3 w-32">
            <div class="h-4 bg-slate-200 rounded"></div>
          </td>
          <td class="p-3 flex-1">
            <div class="h-4 bg-slate-200 rounded mb-2"></div>
            <div class="h-4 bg-slate-200 rounded w-1/2"></div>
          </td>
          <td class="p-3 hidden md:table-cell">
            <div class="h-4 bg-slate-200 rounded"></div>
          </td>
          <td class="p-3 hidden lg:table-cell">
            <div class="h-4 bg-slate-200 rounded"></div>
          </td>
          <td class="p-3 hidden lg:table-cell">
            <div class="h-4 bg-slate-200 rounded"></div>
          </td>
        </tr>
      </tbody>
    </table>
    </div>
    </div>
  </div>

  <div v-if="modalOpen"
    class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 backdrop-blur px-4 py-8">
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-full overflow-auto">
      <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div>
          <h2 class="text-xl font-semibold">Transaction Details</h2>
          <p class="text-sm text-slate-500 dark:text-slate-300">
            Link this transaction to a supplier or requisition and capture finance notes.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <EditButton v-if="!modalEditing" @edit="enableEditing" />
          <button class="btn" @click.prevent="closeTransactionModal">
            Close
          </button>
        </div>
      </div>
      <div class="px-6 py-6 space-y-6" v-if="activeTransaction">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div class="text-xs uppercase text-slate-500">Date</div>
            <div class="font-semibold text-lg">{{ formatDate(activeTransaction.transaction_date) }}</div>
          </div>
          <div>
            <div class="text-xs uppercase text-slate-500">Type</div>
            <div v-if="!modalEditing" class="font-semibold">
              {{ activeTransaction.type ?? "—" }}
            </div>
            <input v-else v-model="modalForm.type" class="input w-full mt-2" placeholder="Type" />
            <div class="mt-3">
              <div class="text-xs uppercase text-slate-500">Flow</div>
              <div v-if="!modalEditing" class="font-semibold mt-1">
                {{ modalForm.flow === "in" ? "In" : "Out" }}
              </div>
              <div v-else class="flex gap-2 mt-2">
                <button class="btn" :class="{ accent: modalForm.flow === 'in' }"
                  @click.prevent="modalForm.flow = 'in'">
                  In
                </button>
                <button class="btn" :class="{ accent: modalForm.flow === 'out' }"
                  @click.prevent="modalForm.flow = 'out'">
                  Out
                </button>
              </div>
            </div>
          </div>
          <div>
            <div class="text-xs uppercase text-slate-500">Amount</div>
            <div class="font-semibold">
              <span v-if="modalEditing">
                {{ formatCurrency(modalForm.amount) }} {{ modalForm.flow === "in" ? "In" : "Out" }}
              </span>
              <template v-else>
                <span v-if="activeTransaction.paid_in">{{ formatCurrency(activeTransaction.paid_in) }} In</span>
                <span v-else-if="activeTransaction.paid_out">{{ formatCurrency(activeTransaction.paid_out) }} Out</span>
                <span v-else>—</span>
              </template>
            </div>
          </div>
        </div>
        <div class="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-900">
          <div class="text-xs uppercase text-slate-500">Description</div>
          <div class="font-semibold text-slate-800 dark:text-white">
            {{ activeTransaction.description }}
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="text-sm font-medium text-slate-700 dark:text-slate-200">Supplier</div>
              <button v-if="modalForm.supplier && modalEditing" class="text-xs text-rose-600 hover:underline"
                @click.prevent="clearSupplier">
                Remove
              </button>
            </div>
            <div class="text-sm text-slate-500" v-if="!modalForm.supplier">
              Not linked
            </div>
            <div v-else class="px-3 py-2 rounded bg-slate-100 dark:bg-slate-900 text-sm">
              {{ modalForm.supplier.company }}
            </div>
            <SearchableInput v-if="modalEditing" :key="`supplier-${supplierInputKey}`" class="w-full"
              url="/api/supplier/search?query=" :output="supplierOutput" placeholder="Search suppliers"
              @selected="selectSupplier" />
          </div>

          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="text-sm font-medium text-slate-700 dark:text-slate-200">Requisition</div>
              <button v-if="modalForm.requisition && modalEditing" class="text-xs text-rose-600 hover:underline"
                @click.prevent="clearRequisition">
                Remove
              </button>
            </div>
            <div class="text-sm text-slate-500" v-if="!modalForm.requisition">
              Not linked
            </div>
            <div v-else class="px-3 py-2 rounded bg-slate-100 dark:bg-slate-900 text-sm">
              #{{ modalForm.requisition.id }} · {{ modalForm.requisition.description }}
            </div>
            <SearchableInput v-if="modalEditing" :key="`requisition-${requisitionInputKey}`" class="w-full"
              url="/api/requisition/search?query=" :output="requisitionOutput" placeholder="Search requisitions"
              @selected="selectRequisition" />
          </div>
        </div>

        <div class="space-y-2">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-200">Notes</label>
          <textarea v-model="modalForm.notes" class="input min-h-[120px] px-4 py-2 w-full"
            :readonly="!modalEditing"
            placeholder="Internal notes or categorisation guidance"></textarea>
        </div>

        <div class="flex justify-end gap-3">
          <button class="btn" @click.prevent="closeTransactionModal">
            Cancel
          </button>
          <button v-if="modalEditing" class="btn accent" :disabled="modalSaving"
            @click.prevent="saveTransactionDetails">
            {{ modalSaving ? "Saving…" : "Save Changes" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bank-transactions-workspace {
  container: reference-workspace / inline-size;
}
</style>
